import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Globe,
  Heart,
  CheckCircle,
  ArrowRight,
  Quote,
  Target,
  Brain,
  RefreshCw,
  XCircle,
  ChevronRight,
  Map,
  Users,
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useTranslation } from "react-i18next";

// --- BULLET ---
const Bullet = ({ children, icon: Icon = CheckCircle, color = "text-warm-sage" }) => (
  <li className="flex items-start gap-3 mb-3">
    <Icon className={`h-5 w-5 ${color} mt-0.5 flex-shrink-0`} />
    <span className="text-charcoal/90 leading-relaxed text-sm md:text-base">
      {children}
    </span>
  </li>
);

// --- QUIZ ---
const SelfDiscoveryQuiz = ({ search }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [score, setScore] = useState(0);

  const questions = [
    t("ancrage.quiz.q1", {
      defaultValue: "Avez-vous déjà eu le sentiment d’être « de trop » dans un pays ou un groupe ?"
    }),
    t("ancrage.quiz.q2", {
      defaultValue:
        "Adaptez-vous souvent votre manière de parler ou de vous comporter selon le contexte (famille, pays, entreprise) ?"
    }),
    t("ancrage.quiz.q3", {
      defaultValue: "Vous arrive-t-il de vous sentir étranger·ère même parmi les vôtres ?"
    })
  ];

  const handleAnswer = (value) => {
    setScore((s) => s + (value ? 1 : 0));
    if (step < questions.length - 1) {
      setStep((s) => s + 1);
    } else {
      setDone(true);
    }
  };

  const reset = () => {
    setStep(0);
    setScore(0);
    setDone(false);
  };

  if (done) {
    return (
      <Card className="p-6 md:p-8 bg-gradient-to-br from-warm-sage/20 to-soft-taupe/10 border-warm-sage/30 shadow-xl text-center">
        <Map className="h-12 w-12 text-warm-sage mx-auto mb-4" />
        <h3 className="text-xl md:text-2xl font-serif text-charcoal mb-2">
          {t("ancrage.quiz.result.title", { defaultValue: "Votre carte intérieure commence ici" })}
        </h3>
        <p className="text-sm md:text-base text-charcoal/80 mb-6 leading-relaxed">
          {score >= 2
            ? t("ancrage.quiz.result.high", {
                defaultValue:
                  "Votre vécu interculturel est riche et parfois lourd à porter. Cet espace peut vous aider à transformer ce poids en ressource structurée."
              })
            : t("ancrage.quiz.result.low", {
                defaultValue:
                  "Vous êtes déjà sur un chemin d’équilibre. Un accompagnement peut vous permettre de nommer plus finement vos codes et d’en faire un atout conscient."
              })}
        </p>
        <a href={`/${search}#contact`}>
          <Button className="bg-warm-sage text-cream-white w-full rounded-full text-sm md:text-base hover:bg-warm-sage/90">
            {t("ancrage.quiz.cta", { defaultValue: "Explorer mon parcours" })}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </a>
        <Button
          variant="ghost"
          onClick={reset}
          className="text-warm-gray-600 mt-3 text-xs md:text-sm"
        >
          <RefreshCw className="mr-1 h-3 w-3" /> {t("ancrage.quiz.reset", { defaultValue: "Refaire le test" })}
        </Button>
      </Card>
    );
  }

  return (
    <Card className="p-6 md:p-8 bg-white/95 backdrop-blur-sm border-warm-sage/20 shadow-xl">
      <div className="mb-4 md:mb-5">
        <div className="flex justify-between items-center mb-2">
          <span className="text-[11px] md:text-xs text-warm-gray-600 tracking-wider uppercase">
            {t("ancrage.quiz.kicker", { defaultValue: "Où en est votre sentiment d’appartenance ?" })}
          </span>
          <span className="text-[11px] md:text-xs text-warm-gray-600">
            {t("ancrage.quiz.progress", {
              defaultValue: "Question {{n}} / {{total}}",
              n: step + 1,
              total: questions.length
            })}
          </span>
        </div>
        <div className="w-full bg-soft-gray/30 rounded-full h-1.5">
          <div
            className="bg-warm-sage h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${((step + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>
      <p className="font-serif text-charcoal text-center text-lg md:text-xl leading-relaxed mb-5 md:mb-6 min-h-[72px]">
        {questions[step]}
      </p>
      <div className="flex gap-3">
        <Button
          onClick={() => handleAnswer(true)}
          className="flex-1 bg-warm-sage text-cream-white rounded-full text-sm md:text-base hover:bg-warm-sage/90"
        >
          {t("ancrage.quiz.yes", { defaultValue: "Oui" })}
        </Button>
        <Button
          onClick={() => handleAnswer(false)}
          variant="outline"
          className="flex-1 border-soft-taupe text-charcoal rounded-full text-sm md:text-base"
        >
          {t("ancrage.quiz.no", { defaultValue: "Non" })}
        </Button>
      </div>
    </Card>
  );
};

// --- PAGE PRINCIPALE ---
export default function ArchitecteAncrage() {
  const { t } = useTranslation();
  const { search } = useLocation();
  const [activeTab, setActiveTab] = useState("avant");

  return (
    <section className="bg-cream-white">
      {/* HERO */}
      <div className="relative h-[55vh] md:h-[60vh] min-h-[360px] w-full flex items-end pb-10 md:pb-16">
        <img
          src="https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=2000&q=80"
          alt={t("ancrage.hero.alt")}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/75 via-charcoal/45 to-transparent z-10" />
        <div className="absolute top-0 inset-x-0 h-20 bg-gradient-to-b from-charcoal/40 to-transparent z-10" />

        <div className="relative max-w-6xl mx-auto px-6 w-full z-20 pt-10 md:pt-14">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-cream-white/20 backdrop-blur-sm border border-cream-white/30">
              <Globe className="h-3 w-3 md:h-4 md:w-4 text-cream-white" />
              <span className="text-cream-white/90 text-[10px] md:text-xs tracking-[0.25em] uppercase">
                {t("ancrage.hero.badge")}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-light text-cream-white mb-3 md:mb-4 leading-tight">
              {t("ancrage.hero.title")}
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-cream-white/90 mb-3 max-w-2xl font-light">
              {t("ancrage.hero.line1")}
            </p>
            <p className="text-xs sm:text-sm md:text-base text-cream-white/85 mb-5 max-w-xl">
              {t("ancrage.hero.line2")}
            </p>

            {/* Subnav chips */}
            <div className="flex flex-wrap gap-2 mb-4">
              <a
                href="#scope"
                className="text-[11px] md:text-xs px-3 py-1.5 rounded-full border border-cream-white/70 text-cream-white/95 hover:bg-cream-white/10"
              >
                {t("ancrage.hero.scope")}
              </a>
              <a
                href="#risques"
                className="text-[11px] md:text-xs px-3 py-1.5 rounded-full border border-cream-white/70 text-cream-white/95 hover:bg-cream-white/10"
              >
                {t("ancrage.hero.prevention")}
              </a>
              <a
                href={`/${search}#contact`}
                className="text-[11px] md:text-xs px-3 py-1.5 rounded-full border border-cream-white/70 text-cream-white/95 hover:bg-cream-white/10"
              >
                {t("ancrage.hero.contact")}
              </a>
            </div>

            <a href={`/${search}#contact`}>
              <Button className="bg-warm-sage hover:bg-warm-sage/90 text-cream-white px-6 md:px-8 py-2.5 md:py-3 text-sm md:text-base rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5">
                {t("ancrage.cta.primary")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-6xl mx-auto px-6 py-14 md:py-20">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-2 space-y-8">
            {/* INTRO */}
            <Card className="p-6 md:p-8 bg-white/95 border-soft-gray/20 shadow-xl">
              <div className="flex items-start gap-4 md:gap-5">
                <Users className="h-8 w-8 md:h-10 md:w-10 text-warm-sage flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl md:text-3xl font-serif text-charcoal mb-3 md:mb-4">
                    {t("ancrage.intro.title")}
                  </h2>
                  <p className="text-sm md:text-lg text-charcoal/80 leading-relaxed mb-4 md:mb-5">
                    {t("ancrage.intro.p1")}
                  </p>
                  <p className="text-sm md:text-lg text-charcoal/80 leading-relaxed mb-4">
                    {t("ancrage.intro.p2")}
                  </p>
                  <p className="text-sm md:text-lg text-charcoal/80 leading-relaxed">
                    {t("ancrage.intro.p3.before")}{" "}
                    <span className="italic font-medium">
                      {t("ancrage.intro.p3.quote")}
                    </span>
                  </p>
                </div>
              </div>
            </Card>

            {/* SCOPE */}
            <Card id="scope" className="p-6 md:p-7 bg-white/95 border-soft-gray/30 shadow-lg">
              <h3 className="text-lg md:text-xl font-semibold text-charcoal mb-3">
                {t("ancrage.scope.title")}
              </h3>
              <p className="text-sm md:text-base text-charcoal/85 leading-relaxed mb-4">
                {t("ancrage.scope.p")}
              </p>
              <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                <div className="rounded-lg border border-soft-gray/30 p-3">
                  <p className="text-sm text-charcoal/90">
                    {t("ancrage.scope.items.0")}
                  </p>
                </div>
                <div className="rounded-lg border border-soft-gray/30 p-3">
                  <p className="text-sm text-charcoal/90">
                    {t("ancrage.scope.items.1")}
                  </p>
                </div>
                <div className="rounded-lg border border-soft-gray/30 p-3">
                  <p className="text-sm text-charcoal/90">
                    {t("ancrage.scope.items.2")}
                  </p>
                </div>
                <div className="rounded-lg border border-soft-gray/30 p-3">
                  <p className="text-sm text-charcoal/90">
                    {t("ancrage.scope.items.3")}
                  </p>
                </div>
              </div>
              <p className="text-[11px] md:text-xs text-warm-gray mt-4 leading-relaxed">
                {t("ancrage.scope.note")}
              </p>
            </Card>

            {/* PREVENTION / RISKS */}
            <Card id="risques" className="p-6 md:p-7 bg-gradient-to-br from-rose-50 via-cream-white to-soft-taupe/20 border border-rose-100 shadow-md relative overflow-hidden">
              <div className="pointer-events-none absolute -right-10 -top-10 w-40 h-40 rounded-full bg-rose-200/40 blur-3xl opacity-70" />
              <div className="relative flex flex-col md:flex-row gap-6 md:gap-8 items-start">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-rose-100/70 border border-rose-200">
                    <XCircle className="h-4 w-4 text-rose-600" />
                    <span className="text-[11px] md:text-xs tracking-[0.18em] uppercase text-rose-700">
                      {t("ancrage.risks.kicker")}
                    </span>
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-charcoal mb-3">
                    {t("ancrage.risks.title")}
                  </h3>
                  <p className="text-sm md:text-base text-charcoal/85 leading-relaxed mb-3">
                    {t("ancrage.risks.p1")}
                  </p>
                  <p className="text-xs md:text-sm text-charcoal/80 leading-relaxed mb-3">
                    {t("ancrage.risks.p2")}
                  </p>
                  <p className="text-[11px] md:text-xs text-warm-gray leading-relaxed">
                    {t("ancrage.risks.note.part1")}{" "}
                    <span className="font-medium">{t("ancrage.risks.note.bold")}</span>
                    {t("ancrage.risks.note.part2")}
                  </p>
                </div>
                <div className="w-full md:w-64 lg:w-72">
                  <div className="rounded-xl bg-white/80 border border-soft-gray/40 p-4 md:p-5 shadow-sm">
                    <p className="text-xs md:text-sm font-medium text-charcoal mb-3 flex items-center gap-2">
                      <Brain className="h-4 w-4 text-warm-sage" />
                      {t("ancrage.risks.signals.title")}
                    </p>
                    <ul className="space-y-2.5 text-xs md:text-sm text-charcoal/85">
                      <li className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-rose-500 flex-shrink-0" />
                        <span>{t("ancrage.risks.signals.s1")}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                        <span>{t("ancrage.risks.signals.s2")}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-warm-sage flex-shrink-0" />
                        <span>{t("ancrage.risks.signals.s3")}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-soft-taupe flex-shrink-0" />
                        <span>{t("ancrage.risks.signals.s4")}</span>
                      </li>
                    </ul>
                    <div className="mt-4 pt-3 border-t border-soft-gray/30">
                      <p className="text-[11px] md:text-xs text-warm-gray leading-relaxed">
                        {t("ancrage.risks.signals.footer")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* AVANT / APRÈS */}
            <Card className="p-6 md:p-8 bg-gradient-to-br from-white to-soft-taupe/5 border-warm-sage/20 shadow-lg">
              <div className="flex border-b border-soft-gray/20 mb-5 md:mb-6">
                <button
                  onClick={() => setActiveTab("avant")}
                  className={`flex-1 py-2.5 md:py-3 text-sm md:text-base font-medium transition-colors duration-300 ${
                    activeTab === "avant" ? "text-warm-sage border-b-2 border-warm-sage" : "text-charcoal/60 hover:text-charcoal"
                  }`}
                >
                  {t("ancrage.beforeAfter.before")}
                </button>
                <button
                  onClick={() => setActiveTab("apres")}
                  className={`flex-1 py-2.5 md:py-3 text-sm md:text-base font-medium transition-colors duration-300 ${
                    activeTab === "apres" ? "text-warm-sage border-b-2 border-warm-sage" : "text-charcoal/60 hover:text-charcoal"
                  }`}
                >
                  {t("ancrage.beforeAfter.after")}
                </button>
              </div>

              {activeTab === "avant" && (
                <div className="space-y-3 md:space-y-4">
                  <Bullet icon={XCircle} color="text-rose-500">{t("ancrage.beforeAfter.beforeList.0")}</Bullet>
                  <Bullet icon={XCircle} color="text-rose-500">{t("ancrage.beforeAfter.beforeList.1")}</Bullet>
                  <Bullet icon={XCircle} color="text-rose-500">{t("ancrage.beforeAfter.beforeList.2")}</Bullet>
                </div>
              )}

              {activeTab === "apres" && (
                <div className="space-y-3 md:space-y-4">
                  <Bullet icon={CheckCircle} color="text-green-600">{t("ancrage.beforeAfter.afterList.0")}</Bullet>
                  <Bullet icon={CheckCircle} color="text-green-600">{t("ancrage.beforeAfter.afterList.1")}</Bullet>
                  <Bullet icon={CheckCircle} color="text-green-600">{t("ancrage.beforeAfter.afterList.2")}</Bullet>
                </div>
              )}
            </Card>

            {/* CTA */}
            <Card className="p-6 md:p-8 bg-white border-soft-gray/20 shadow-lg text-center">
              <h3 className="text-xl md:text-2xl font-serif text-charcoal mb-4">
                {t("ancrage.cta.title")}
              </h3>
              <p className="text-sm md:text-base text-charcoal/80 mb-6">
                {t("ancrage.cta.body")}
              </p>
              <a href={`/${search}#contact`}>
                <Button className="bg-warm-sage hover:bg-warm-sage/90 text-cream-white px-8 md:px-10 py-2.5 md:py-3 text-sm md:text-base rounded-full shadow-lg hover:shadow-xl transition-all">
                  {t("ancrage.cta.primary")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </Card>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-8 lg:sticky lg:top-24">
            <SelfDiscoveryQuiz search={search} />
            <Card className="p-6 bg-white/80 backdrop-blur-sm border-soft-taupe/30 shadow-lg">
              <div className="text-center mb-4">
                <Quote className="h-6 w-6 md:h-8 md:w-8 text-warm-sage mx-auto" />
              </div>
              <p className="text-sm md:text-base text-charcoal/80 italic text-center mb-4 md:mb-5 leading-relaxed">
                {t("ancrage.testimonial.quote")}
              </p>
              <div className="flex items-center justify-center gap-3">
               {/* <img
                  src="https://images.pexels.com/photos/7710087/pexels-photo-7710087.jpeg?auto=format&fit=crop&w=300&q=80"
                  alt={t("ancrage.testimonial.alt")}
                  className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-gray-200 object-cover border-2 border-warm-sage/40"
                />*/}
                <div>
                  <p className="text-xs md:text-sm font-medium text-charcoal">{t("ancrage.testimonial.name")}</p>
                  <p className="text-[11px] text-warm-gray">{t("ancrage.testimonial.role")}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
